/**
 * Service pour récupérer les vidéos depuis les endpoints AdultContentController
 */

import { api } from './api'
import { Content } from './homepage'

// Types pour les réponses des différents providers
interface EpornerThumbnail {
  size?: string
  width?: number
  height?: number
  src?: string
}

interface EpornerVideo {
  id?: string
  vid?: string
  title?: string
  keywords?: string
  duration?: string
  views?: number
  thumb?: string
  default_thumb?: string | EpornerThumbnail
}

interface LetsJerktvVideo {
  id?: string
  url?: string
  video_link?: string // Structure réelle du backend
  title?: string
  description?: string
  duration?: number | string // Format "33:14" ou nombre en secondes
  thumbnail?: string
  thumb?: string
  rating?: string
  quality?: string
}

interface RedtubeVideo {
  video?: {
    video_id?: string
    title?: string
    duration?: string
    thumb?: string
    default_thumb?: string
  }
  // Structure directe renvoyée par le backend
  video_id?: string
  title?: string
  duration?: string
  thumb?: string
  default_thumb?: string
  views?: number
  rating?: number
  url?: string
  embed_url?: string
  publish_date?: string
}

interface PornhubVideo {
  video_id?: string
  title?: string
  duration?: string
  thumb?: string
  default_thumb?: string
}

/**
 * Normalise une vidéo Eporner vers le format Content
 */
export function normalizeEpornerVideo(video: EpornerVideo, _index: number): Content | null {
  if (!video.vid && !video.id) return null
  
  const videoId = video.vid || video.id || ''
  const duration = video.duration ? parseDuration(video.duration) : undefined
  
  // Extraire l'URL de la miniature depuis default_thumb
  let thumbnailUrl: string | undefined
  if (video.default_thumb) {
    if (typeof video.default_thumb === 'string') {
      thumbnailUrl = video.default_thumb
    } else if (typeof video.default_thumb === 'object' && video.default_thumb.src) {
      thumbnailUrl = video.default_thumb.src
    }
  }
  
  // Fallback sur thumb si default_thumb n'est pas disponible
  if (!thumbnailUrl) {
    thumbnailUrl = video.thumb || undefined
  }

  return {
    id: `eporner_${videoId}`,
    title: video.title || 'Sans titre',
    // Ne plus utiliser keywords comme description
    description: undefined,
    status: 'approved',
    createdAt: new Date().toISOString(),
    thumbnailS3Key: thumbnailUrl,
    duration,
    views: video.views,
    category: 'adult',
    isPremium: false,
    provider: 'eporner',
    providerVideoId: videoId,
  }
}

/**
 * Normalise une vidéo LetsJerktv vers le format Content
 */
export function normalizeLetsJerktvVideo(video: LetsJerktvVideo, index: number): Content | null {
  // Le backend renvoie video_link au lieu de url
  const videoLink = video.video_link || video.url
  if (!video.id && !videoLink) return null
  
  // Extraire l'ID depuis video_link (ex: "https://letsjerk.tv/slug/" -> "slug")
  const videoId = video.id || extractIdFromUrl(videoLink) || `video_${index}`
  const videoUrl = videoLink || undefined
  
  // Parser la durée qui peut être au format "33:14" (string) ou un nombre
  let duration: number | undefined
  if (video.duration) {
    if (typeof video.duration === 'string') {
      duration = parseDuration(video.duration)
    } else {
      duration = video.duration
    }
  }

  const normalizedContent = {
    id: `letsjerktv_${videoId}`,
    title: video.title || 'Sans titre',
    description: video.description || undefined,
    status: 'approved',
    createdAt: new Date().toISOString(),
    thumbnailS3Key: video.thumbnail || video.thumb || undefined,
    duration,
    category: 'adult',
    isPremium: false,
    provider: 'letsjerktv' as const,
    providerVideoId: videoId,
    providerVideoUrl: videoUrl,
  }
  
  // Debug pour vérifier que l'ID est bien formaté
  console.log('LetsJerkTV vidéo normalisée:', {
    id: normalizedContent.id,
    videoId,
    videoUrl,
    title: normalizedContent.title
  })
  
  return normalizedContent
}

/**
 * Normalise une vidéo Redtube vers le format Content
 */
export function normalizeRedtubeVideo(video: RedtubeVideo, _index: number): Content | null {
  // Le backend renvoie directement les propriétés dans l'objet vidéo, pas dans video.video
  const videoId = video.video_id || video.video?.video_id
  if (!videoId) return null
  
  // Utiliser les propriétés directes ou celles dans video.video
  const title = video.title || video.video?.title
  const durationStr = video.duration || video.video?.duration
  const duration = durationStr ? parseDuration(durationStr) : undefined
  const thumbnail = video.default_thumb || video.thumb || video.video?.default_thumb || video.video?.thumb

  const normalizedContent = {
    id: `redtube_${videoId}`,
    title: title || 'Sans titre',
    status: 'approved',
    createdAt: new Date().toISOString(),
    thumbnailS3Key: thumbnail,
    duration,
    views: video.views,
    category: 'adult',
    isPremium: false,
    provider: 'redtube' as const,
    providerVideoId: videoId.toString(),
  }
  
  // Debug pour vérifier que l'ID est bien formaté
  console.log('Redtube vidéo normalisée:', {
    id: normalizedContent.id,
    videoId: normalizedContent.providerVideoId,
    title: normalizedContent.title
  })
  
  return normalizedContent
}

/**
 * Normalise une vidéo Pornhub vers le format Content
 */
function normalizePornhubVideo(video: PornhubVideo, _index: number): Content | null {
  if (!video.video_id) return null
  
  const duration = video.duration ? parseDuration(video.duration) : undefined

  return {
    id: `pornhub_${video.video_id}`,
    title: video.title || 'Sans titre',
    status: 'approved',
    createdAt: new Date().toISOString(),
    thumbnailS3Key: video.default_thumb || video.thumb || undefined,
    duration,
    category: 'adult',
    isPremium: false,
  }
}

/**
 * Parse une durée au format string (ex: "12:34" ou "1234") vers des secondes
 */
function parseDuration(duration: string | number): number | undefined {
  if (typeof duration === 'number') return duration
  
  const parts = duration.toString().split(':')
  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10)
    const seconds = parseInt(parts[1], 10)
    if (!isNaN(minutes) && !isNaN(seconds)) {
      return minutes * 60 + seconds
    }
  }
  
  // Essayer de parser comme un nombre de secondes
  const seconds = parseInt(duration.toString(), 10)
  if (!isNaN(seconds)) return seconds
  
  return undefined
}

/**
 * Extrait un ID depuis une URL
 * Pour LetsJerkTV: "https://letsjerk.tv/slug/" -> "slug"
 * Pour autres: extrait le dernier segment de l'URL
 */
export function extractIdFromUrl(url?: string): string | null {
  if (!url) return null
  
  // Pour LetsJerkTV, extraire le slug après letsjerk.tv/
  // Gérer les URLs avec ou sans trailing slash
  if (url.includes('letsjerk.tv/')) {
    // Extraire le slug, en gérant le trailing slash
    const match = url.match(/letsjerk\.tv\/([^\/\?]+)/)
    if (match && match[1]) {
      return match[1].replace(/\/$/, '') // Retirer le trailing slash si présent
    }
    return null
  }
  
  // Pour les autres URLs, extraire le dernier segment
  const match = url.match(/\/([^\/\?]+)\/?$/)
  return match ? match[1].replace(/\/$/, '') : null
}

/**
 * Récupère 8 vidéos depuis Eporner
 */
export async function getEpornerVideos(): Promise<Content[]> {
  try {
    const response = await api.get('/adult-content/eporner/search', {
      params: {
        query: 'all',
        per_page: 8,
        page: 1,
        order: 'latest',
      },
    })
    
    const videos = response.data?.videos || response.data?.data || response.data || []
    return videos
      .map((video: EpornerVideo, index: number) => normalizeEpornerVideo(video, index))
      .filter((video: Content | null): video is Content => video !== null)
      .slice(0, 8)
  } catch (error) {
    console.error('Erreur lors de la récupération des vidéos Eporner:', error)
    return []
  }
}

/**
 * Récupère 8 vidéos depuis LetsJerktv via la route /api/adult-content/letsjerktv/feed
 * Retourne exactement 8 vidéos : 3 gratuites (basic) et 5 premium/vip
 */
export async function getLetsJerktvVideos(): Promise<Content[]> {
  try {
    // Utiliser la route /api/adult-content/letsjerktv/feed
    const response = await api.get('/adult-content/letsjerktv/feed', {
      params: {
        page: 1,
      },
    })
    
    // La réponse contient un champ 'videos' selon LetsJerktvFeedResponse
    const videos = response.data?.videos || response.data?.data || (Array.isArray(response.data) ? response.data : [])
    
    // Normaliser et limiter à exactement 8 vidéos
    const normalizedVideos = videos
      .map((video: LetsJerktvVideo, index: number) => normalizeLetsJerktvVideo(video, index))
      .filter((video: Content | null): video is Content => video !== null)
      .slice(0, 8)
    
    // S'assurer qu'on a exactement 8 vidéos (3 gratuites + 5 premium/vip)
    if (normalizedVideos.length < 8) {
      console.warn(`Seulement ${normalizedVideos.length} vidéos LetsJerktv récupérées, attendu 8`)
    }
    
    return normalizedVideos
  } catch (error) {
    console.error('Erreur lors de la récupération des vidéos LetsJerktv:', error)
    return []
  }
}

/**
 * Récupère 8 vidéos depuis Redtube
 */
export async function getRedtubeVideos(): Promise<Content[]> {
  try {
    const response = await api.get('/adult-content/redtube/search', {
      params: {
        page: 1,
      },
    })
    
    const videos = response.data?.videos || response.data?.data || response.data || []
    return videos
      .map((video: RedtubeVideo, index: number) => normalizeRedtubeVideo(video, index))
      .filter((video: Content | null): video is Content => video !== null)
      .slice(0, 8)
  } catch (error) {
    console.error('Erreur lors de la récupération des vidéos Redtube:', error)
    return []
  }
}

/**
 * Récupère 5 vidéos depuis Pornhub
 */
export async function getPornhubVideos(): Promise<Content[]> {
  try {
    const response = await api.get('/adult-content/pornhub/search', {
      params: {},
    })
    
    const videos = response.data?.videos || response.data?.data || response.data || []
    return videos
      .map((video: PornhubVideo, index: number) => normalizePornhubVideo(video, index))
      .filter((video: Content | null): video is Content => video !== null)
      .slice(0, 5)
  } catch (error) {
    console.error('Erreur lors de la récupération des vidéos Pornhub:', error)
    return []
  }
}

/**
 * Récupère toutes les vidéos de tous les providers
 */
export async function getAllProviderVideos(): Promise<{
  eporner: Content[]
  letsjerktv: Content[]
  redtube: Content[]
}> {
  const results = await Promise.allSettled([
    getEpornerVideos(),
    getLetsJerktvVideos(),
    getRedtubeVideos(),
  ])
  
  const eporner = results[0].status === 'fulfilled' ? results[0].value : []
  const letsjerktv = results[1].status === 'fulfilled' ? results[1].value : []
  const redtube = results[2].status === 'fulfilled' ? results[2].value : []
  
  return { eporner, letsjerktv, redtube }
}

/**
 * Récupère les détails d'une vidéo Eporner
 */
export async function getEpornerVideoDetails(videoId: string): Promise<any> {
  try {
    const response = await api.get(`/adult-content/eporner/video/${videoId}`)
    return response.data
  } catch (error) {
    console.error('Erreur lors de la récupération des détails de la vidéo Eporner:', error)
    throw error
  }
}

/**
 * Récupère les détails d'une vidéo LetsJerktv
 */
export async function getLetsJerktvVideoDetails(videoUrl: string): Promise<any> {
  try {
    const response = await api.get('/adult-content/letsjerktv/video-information', {
      params: { video_url: videoUrl }
    })
    return response.data
  } catch (error) {
    console.error('Erreur lors de la récupération des détails de la vidéo LetsJerktv:', error)
    throw error
  }
}

/**
 * Récupère les détails d'une vidéo Redtube
 */
export async function getRedtubeVideoDetails(videoId: string): Promise<any> {
  try {
    const response = await api.get(`/adult-content/redtube/video/${videoId}`)
    return response.data
  } catch (error) {
    console.error('Erreur lors de la récupération des détails de la vidéo Redtube:', error)
    throw error
  }
}

/**
 * Extrait le provider et l'ID depuis un ID de vidéo
 */
export function parseVideoId(id: string | number): { provider: 'eporner' | 'letsjerktv' | 'redtube' | 'pornhub' | 'local', videoId: string } {
  const idStr = id.toString()
  
  if (idStr.startsWith('eporner_')) {
    return { provider: 'eporner', videoId: idStr.replace('eporner_', '') }
  }
  if (idStr.startsWith('letsjerktv_')) {
    return { provider: 'letsjerktv', videoId: idStr.replace('letsjerktv_', '') }
  }
  if (idStr.startsWith('redtube_')) {
    return { provider: 'redtube', videoId: idStr.replace('redtube_', '') }
  }
  if (idStr.startsWith('pornhub_')) {
    return { provider: 'pornhub', videoId: idStr.replace('pornhub_', '') }
  }
  
  // Par défaut, considérer comme une vidéo locale
  return { provider: 'local', videoId: idStr }
}

